import {Component, HostListener, OnDestroy, OnInit, isDevMode, Inject} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {LoginService} from './core/login.service';
import {UiToolsService} from './core/ui-tools.service';
import {CmsApiService} from './core/cms-api.service';
import {CmsErrorHandlerService} from './core/cms-error-handler.service';
import {CmsQueueService} from './core/cms-queue.service';
import {TranslateService} from '@ngx-translate/core';
import {CommonsService} from './core/commons.service';
import {Subscription} from 'rxjs';
import {UserData} from './core/definitions/user-data';
import {SwUpdate} from '@angular/service-worker';
import {PrimusBackendInstanceService} from './core/primus-backend-instance.service';
import {ModelsService} from './core/models.service';
import {SearchPageService} from './object-search/search-page.service';
import {JobStatusSubscriberService} from './core/jobstatussubcriber/job-status-subscriber.service';
import {UserCacheService} from './core/user-cache.service';
import {MatLegacySnackBar as MatSnackBar} from '@angular/material/legacy-snack-bar';
import {ClientConfig} from './core/definitions/client-config';
import {PrimusRouterService} from './core/primus-router.service';
import {PrimusRouteService} from './core/primus-route.service';
import {distinctUntilChanged, take} from "rxjs/operators";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {AlertDialogComponent} from "./shared/alert-dialog/alert-dialog.component";
import {ConfirmDialogData} from "./object-edit/confirm-dialog/confirm-dialog.component";
import {SettingsService} from "./core/settings.service";
import {FieldConditionService} from "./core/field-condition.service";
import {ValueOptionService} from "./core/value-option.service";
import {FieldDateInfoService} from "./core/field-date-info.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {

  public currentStateName;
  public authErr: string;
  public txtLoadingContents: string;
  public txtToLogin: string;
  public hideLoader: boolean;

  private txtApiAuthenticationFailed: string;
  private txtNewVersionAvailable: string;
  private txtNewVersionDownload: string;

  private user: UserData;
  private loginComplete: boolean;
  private authState = {
    user: false,
    api: false
  };
  private apiAuthRetryCounter = 0;
  private apiAuthRetryMaxCount = 10;
  private apiAuthRetryMs = 100;
  private authenticating = false;

  showApiSelector: boolean;
  private newVersionAvailable: boolean;

  private subscription: Subscription = new Subscription();

  className: string;
  private languageSet = false;

  constructor(
    public primusRouter: PrimusRouterService,
    private translate: TranslateService,
    private primusRoute: PrimusRouteService,
    private loginService: LoginService,
    private uiTools: UiToolsService,
    private cms: CmsApiService,
    private cmsQueue: CmsQueueService,
    private cmsErrorHandlerService: CmsErrorHandlerService,
    private commons: CommonsService,
    private models: ModelsService,
    private searchPageService: SearchPageService,
    private readonly primusBackendInstanceService: PrimusBackendInstanceService,
    private readonly swUpdate: SwUpdate,
    private readonly jobStatusSubscriberService: JobStatusSubscriberService,
    private userCacheService: UserCacheService,
    private settingsService: SettingsService,
    private fieldConditions: FieldConditionService,
    private valueOptions: ValueOptionService,
    private fieldDateInfo: FieldDateInfoService,
    private snackBar: MatSnackBar,
    private modalService: MatDialog,
    @Inject(DOCUMENT) private document: Document,
  ) {

    // Updates className with the latest className, updates only on change. This prevents NG0100 Error
    this.subscription.add(
      this.primusRouter.classNameObservable
        .pipe(distinctUntilChanged())
        .subscribe(className => {
          this.className = className;
        })
    )
    this.primusBackendInstanceService.responseStatusSubject
      .pipe(distinctUntilChanged())
      .subscribe((res: Response) => {

        // Reacts to 503 status.
       if(res.status === 503){
         const modalRef = this.modalService.open(AlertDialogComponent, {
           panelClass: 'alert-dialog',
           data: {
             modalTitle: 'TRANS__APP_COMPONENT__SERVICE_UNAVAILABLE',
             modalContent: 'TRANS__EXCEPTION_DEFAULT_503',
             modalButton: 'TRANS__ALERT_DIALOG__RELOAD',
           } as ConfirmDialogData
         })

         modalRef.afterClosed()
           .pipe(
             take(1),
           ).subscribe(() => {
             window.location.reload();
         })
       }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.uiTools.setWindowSize();
  }

  ngOnInit(): void {
    this.setSomeTranslations();

    this.primusBackendInstanceService.authError.subscribe(err => {
      this.showApiSelector = false;
      this.authErr = err;
    });

    this.registerAuthSubscriber();
    this.registerCurrentUserSubscriber();

    if (this.apiAuthRetryCounter < this.apiAuthRetryMaxCount) {
      this.primusBackendInstanceService.setRefreshTokenSetAuthenticated().then();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private async setLanguageAndObjectTypes(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.authState?.api) {
        this.cmsQueue.runCmsFnWithQueue(this.cms.getClientConfig, undefined, false,
          (data: ClientConfig) => {
            // eslint-disable-next-line no-console
            if (isDevMode()) {
              console.debug('[APP] -- cms.getClientConfig() complete');
            }
            if (this.primusRoute.params.lang) {
              this.setTranslationLanguage(this.primusRoute.params.lang)
            } else {
              this.setTranslationLanguage(data.CLIENT_LANGUAGE);
            }
            this.commons.setObjectTypes(data.OBJECT_TYPES);
            this.settingsService.initSettings();
            this.searchPageService.checkCreateSearchContainer();
            this.setSomeTranslations();
            this.fieldConditions.setFieldConditions();
            this.valueOptions.setAllValueOptions();
            this.fieldDateInfo.setFieldDateInfo();
            resolve();
          },
          e => {
            reject(e);
          });
      }
    });
  }

  private setTranslationLanguage(language) {
    this.translate.use(language);
    this.languageSet = true;

    this.document.documentElement.lang = language;
  }

  async hasMainMenu() {
    if ((this.primusRouter.currentState() !== ' ') && (this.currentStateName !== this.primusRouter.currentState())) {
      this.currentStateName = this.primusRouter.currentState();
    }
    const noMenuStates = ['authenticate', 'home', 'home.primus.full-screen']; // , 'home.primus.annotate'
    return this.user && this.primusRouter.currentState() && noMenuStates.indexOf(this.primusRouter.currentState()) === -1;
  }

  registerClick(event) {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.checkForUpdate().then(() => {
        if (this.newVersionAvailable) {
          this.showVersionSnackBar();
        }
      });
    }
    this.uiTools.registerDocumentClick(event);
  }

  // showAuthErr(err: string) {
  //   return err;
  // }

  isAuthenticated() {
    return this.authState.user && this.authState.api && this.loginComplete;
  }

  private init() {
    // eslint-disable-next-line no-console
    if (isDevMode()) {
      console.debug('[APP] -- init()');
    }

    this.currentStateName = this.primusRouter.currentState();

    this.initServiceWorker();

    this.initApp().then();
  }

  private showVersionSnackBar() {
    const versionSnackBar = this.snackBar.open(this.txtNewVersionAvailable, this.txtNewVersionDownload, {
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });

    versionSnackBar.onAction().subscribe(() => {
      window.location.reload();
    });

  }

  private initServiceWorker() {
    try {
      console.log('[SW] Service-worker enabled: ', this.swUpdate.isEnabled);
      if (this.swUpdate.isEnabled) {
        this.swUpdate.checkForUpdate().then(updateFound => {
          console.log('[SW] Update available: ', updateFound);
          this.newVersionAvailable = updateFound;
        });
      }
    } catch (e) {
      console.error('[SW] Unable to subscribe to service-worker registration', e);
    }
  }

  private async initApp() {
    /* eslint-disable no-console */
    if (isDevMode()) {
      console.debug('[APP] -- initApp()');
    }

    try {
      await this.loginService.getUserData();
      await this.models.getModelsAsync();
    } catch (e) {
      console.error(e);
      this.authErr = e.error?.message || 'An unknown error occurred';
      return;
    }

    if (isDevMode()) {
      console.debug('[APP] -- models.getModelsAsync() complete');
    }
    await this.setPostLoginState();

    if (isDevMode()) {
      console.debug('[APP] -- post login state set');
    }

    this.jobStatusSubscriberService.startPolling();
  } /* eslint-enable no-console */

  private async setPostLoginState() {
    if (!this.primusRouter.currentState()) {
      await this.primusRouter.navigateState('home.primus.search', {
        searchText: '',
        searchPage: 1,
        filters: {},
        show_filters: false
      });
    }
    this.loginComplete = true;

    if (this.newVersionAvailable) {
      this.showVersionSnackBar();
    }
  }

  private setSomeTranslations() {
    if (!this.languageSet) {
      let lang = 'no';
      if (navigator.language.toLowerCase().includes('sv')) {
        lang = 'sv';
      }
      this.translate.use(lang);
    }

    this.translate.get('TRANS__APP_COMPONENT__LOADING_CONTENTS').subscribe(s => {
      this.txtLoadingContents = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__AUTHENTICATION_FAILED').subscribe(s => {
      this.txtApiAuthenticationFailed = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__NEW_VERSION_AVAILABLE').subscribe(s => {
      this.txtNewVersionAvailable = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__NEW_VERSION_DOWNLOAD').subscribe(s => {
      this.txtNewVersionDownload = s;
    });

    this.translate.get('TRANS__APP_COMPONENT__BACK_TO_LOGIN').subscribe(s => {
      this.txtToLogin = s;
    });
  }

  private retryApiAuth() {
    const museum = PrimusBackendInstanceService.getInstanceDetails();
    if (this.apiAuthRetryCounter > this.apiAuthRetryMaxCount) {
      // API-authentication failed after multiple retries, inform the user.
      this.authErr = `${museum.name} - ${this.txtApiAuthenticationFailed}`;
      this.hideLoader = true;
      this.showApiSelector = false;
      this.authenticating = false;
    } else {
      try {
        if (this.apiAuthRetryCounter > 0) {
          if (isDevMode()) {
            console.log(`[APP] -- Retrying API-auth in ${this.apiAuthRetryMs}, attempt: ${this.apiAuthRetryCounter}`);
          }
          setTimeout(() => {
            this.primusBackendInstanceService.apiAuthenticate(museum);
          }, this.apiAuthRetryMs);
        } else {
          this.primusBackendInstanceService.apiAuthenticate(museum);
        }
        this.apiAuthRetryCounter++;
      } catch (e) {
        console.error(e);
      }
    }
  }

  private registerCurrentUserSubscriber() {
    this.loginService.currentUser.subscribe(async (userData) => {
      if (!userData) {
        return;
      }

      this.user = userData;

      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.debug('[APP] -- currentUserObservable triggered');
      }

      // Attempt to initialize component only when user data has been loaded.
      this.cms.init((response, params) => {
        this.cmsErrorHandlerService.errHandler(response, params);
      });

      if (isDevMode()) {
        // eslint-disable-next-line no-console
        console.debug('[APP] -- cms.init() complete');
      }
    });
  }

  private registerAuthSubscriber() {
    this.primusBackendInstanceService.authenticated.subscribe(async (state) => {
      if (!state.user && !state.api) {
        return;
      }

      /* eslint-disable no-console */
      if (isDevMode()) {
        console.debug(`[APP] -- user authenticated: ${state.user}`);
        console.debug(`[APP] -- api authenticated: ${state.api}`);
        console.debug(`[APP] -- api auth failed: ${this.authErr}`);
      }
      /* eslint-enable no-console */

      this.authState = state;
      const museum = PrimusBackendInstanceService.getInstanceDetails();

      const showApiSelector = state.user && !state.api && !museum && !this.authenticating;
      const retry = (!state.user || !state.api || this.authenticating || this.authErr !== '') && !showApiSelector;
      const runInit = state.user && state.api && museum && !showApiSelector;

      if (retry) {
        this.showApiSelector = false;
      }

      if (showApiSelector) {
        this.showApiSelector = showApiSelector;
        this.authenticating = true;
        this.apiAuthRetryCounter++;
      } else if (retry) {
        this.retryApiAuth();
      } else if (typeof runInit !== 'undefined') {
        this.hideLoader = true;
        this.init();
      }
      try {
        await this.setLanguageAndObjectTypes();
      } catch (e) {
        this.authErr = e.error?.message || 'An unknown error occurred';
      }
    });
  }
}
